import { Box, Typography } from "@mui/material";
import { QUIZ } from "@/theme/quizTokens";
import { quizAsset } from "@/components/quiz/shared/quizAssets";
import { useProducts, type RawProduct } from "@/state/ProductsContext";

type Supplement = {
  id: string;
  name: string;
  description: string;
  benefit: string;
  image?: string;
};

type Dog = {
  id: string;
  form: { name?: string };
};

type Props = {
  dogs?: Dog[];
};

const SUPPLEMENT_ICONS: Record<string, string> = {
  "salmon oil": "supplement-salmon-oil",
  seaweed: "supplement-seaweed",
  "green lipped mussel": "supplement-green-lipped-mussel",
  turmeric: "supplement-turmeric",
  ginger: "supplement-ginger",
  chia: "supplement-chia",
};

// Exact fallback content — used whenever supplementProducts is empty/unavailable,
// so the section always shows this same set of six supplements in this order.
const SUPPLEMENT_FALLBACK: Supplement[] = [
  {
    id: "salmon-oil",
    name: "Salmon Oil",
    description:
      "Omega-3 and omega-6, for a coat that shines and skin that isn't itching.",
    benefit: "Skin & Coat",
     },
  {
    id: "seaweed",
    name: "Seaweed",
    description:
      "Iodine and trace minerals, the bits a bowl of meat alone tends to miss.",
    benefit: "Coat & Minerals",
  },
  {
    id: "green-lipped-mussel",
    name: "Green Lipped Mussel",
    description:
      "A natural source of glucosamine, for joints that keep up with the zoomies.",
    benefit: "Joints & Mobility",
  
  },
  {
    id: "turmeric",
    name: "Turmeric",
    description:
      "Gently warming, and one of the most studied roots ever to end up in a dog bowl.",
    benefit: "Everyday Wellbeing",
  },
  {
    id: "ginger",
    name: "Ginger",
    description:
      "A classic settler, and turmeric's natural partner in the bowl.",
    benefit: "Settled Tummies",
  },
  {
    id: "chia",
    name: "Chia",
    description:
      "Fibre and plant omegas, for a settled tummy and tidier walks.",
    benefit: "Digestion",
  },
];

function getDogNames(dogs: Dog[] | undefined): string[] {
  return (
    dogs
      ?.map((dog) => dog.form?.name)
      .filter((name): name is string => Boolean(name)) || []
  );
}

// Single dog -> use its actual name (e.g. "Test").
// Multiple dogs -> fall back to a generic plural phrase instead of picking one name.
function getDogDisplayName(names: string[]): string {
  if (names.length === 1) return names[0];
  if (names.length > 1) return "your dogs";
  return "your dog";
}

// Possessive form already includes the trailing apostrophe/'s so it can be
// dropped straight into copy like `${dogPossessive} food` without extra punctuation.
function getDogPossessive(names: string[]): string {
  if (names.length === 1) return `${names[0]}'s`;
  if (names.length > 1) return "your dogs'";
  return "your dog's";
}

function productImage(product: RawProduct): string | undefined {
  return Array.isArray(product.image)
    ? product.image[0]
    : product.image;
}

function supplementFromProduct(
  product: RawProduct
): Supplement {
  return {
    id: String(product.id),
    name: product.title,
    description: product.description || "",
    benefit:
      product.tags?.find(
        (tag) => tag.toLowerCase() !== "supplement"
      ) || "",
    image: productImage(product),
  };
}

function supplementIcon(
  supplement: Supplement
): string | undefined {
  const name = supplement.name
    .toLowerCase()
    .replace(/[^a-z]+/g, " ")
    .trim();

  const assetName = SUPPLEMENT_ICONS[name];

  return assetName ? quizAsset(assetName) : undefined;
}

function SupplementCard({
  supplement,
}: {
  supplement: Supplement;
}) {
  return (
    <Box
      sx={{
        alignItems: "center",
        bgcolor: QUIZ.color.white,
        border: "1px solid rgba(74,48,38,0.14)",
        borderRadius: "12px",
        display: "flex",
        gap: { xs: 1.5, md: 2 },
        minHeight: { xs: 98, md: 112 },
        px: { xs: 1.5, md: 2 },
        py: { xs: 1.25, md: 1.5 },
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          bgcolor: "#B8C58A",
          borderRadius: "9px",
          display: "flex",
          flexShrink: 0,
          height: { xs: 54, md: 64 },
          justifyContent: "center",
          overflow: "hidden",
          width: { xs: 54, md: 64 },
        }}
      >
        <Box
          component="img"
          src={
            supplementIcon(supplement) ||
            supplement.image ||
            quizAsset(`icon-din-${supplement.id}`)
          }
          alt={`${supplement.name} supplement`}
          sx={{
            display: "block",
            height: "100%",
            width: "100%",
            objectFit: "cover",
            objectPosition: "center",
            fontSize: 0,
            color: "transparent",
          }}
        />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            color: QUIZ.color.brown,
            fontFamily: QUIZ.font.display,
            fontSize: { xs: "14px", md: "16px" },
            fontWeight: 900,
            lineHeight: 1.05,
            textTransform: "uppercase",
          }}
        >
          {supplement.name}
        </Typography>

        <Typography
          sx={{
            color: QUIZ.color.ink,
            fontFamily: QUIZ.font.body,
            fontSize: { xs: "12px", md: "14px" },
            lineHeight: 1.35,
            mt: 0.5,
          }}
        >
          {supplement.description}
        </Typography>

        {supplement.benefit && (
          <Box
            component="span"
            sx={{
              bgcolor: QUIZ.color.green,
              borderRadius: "999px",
              color: QUIZ.color.brown,
              display: "inline-block",
              fontFamily: QUIZ.font.button,
              fontSize: { xs: "9px", md: "11px" },
              fontWeight: 800,
              letterSpacing: "0.02em",
              lineHeight: 1,
              mt: 0.75,
              px: 1,
              py: 0.45,
              textTransform: "uppercase",
            }}
          >
            {supplement.benefit}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function ResultsSupplement({
  dogs,
}: Props) {
  const { supplementProducts } = useProducts();

  const dogNames = getDogNames(dogs);
  const dogDisplayName = getDogDisplayName(dogNames);
  const dogPossessive = getDogPossessive(dogNames);

  const dynamicSupplements =
    supplementProducts.map(supplementFromProduct);

  // Fall back to the exact fixed supplement list when no product data is available.
  const supplements =
    dynamicSupplements.length > 0
      ? dynamicSupplements
      : SUPPLEMENT_FALLBACK;

  const supplementCount = supplements.length;

  const supplementIntro =
    supplementCount > 0
      ? `${supplementCount} superfoods`
      : "These superfoods";

  const supplementLabel =
    supplementCount > 0
      ? `${supplementCount} supplements`
      : "these supplements";

  return (
    <Box
      sx={{
        color: QUIZ.color.ink,
        px: { xs: 0, md: 3 },
        py: { xs: 0, md: 2 },
        display: { xs: "block", md: "grid" },
        gridTemplateColumns: { md: "360px 1fr" },
        gridTemplateRows: {
          md: "auto auto auto auto auto",
        },
        columnGap: { md: 6 },
        rowGap: { md: 5 },
        pb: { md: 5 },
        maxWidth: { md: 1080 },
        mx: { md: "auto" },
      }}
    >
      {/* HEADER */}

      <Box
        sx={{
          textAlign: "center",
          gridColumn: { md: "1 / -1" },
          gridRow: { md: "1" },
          maxWidth: { xs: "100%", md: 640 },
          mx: { md: "auto" },
        }}
      >
        <Typography
          sx={{
            color: QUIZ.color.brown,
            fontFamily: QUIZ.font.label,
            fontSize: {
              xs: "0.86rem",
              md: "1rem",
            },
            fontWeight: 700,
            letterSpacing: "0.02em",
            textTransform: "uppercase",
          }}
        >
          Included. Always
        </Typography>

        <Typography
          component="h1"
          sx={{
            color: QUIZ.color.brown,
            fontFamily: QUIZ.font.display,
            fontSize: {
              xs: "33px",
              md: "42px",
            },
            fontWeight: 900,
            lineHeight: 0.94,
            mt: 2,
            textTransform: "uppercase",
          }}
        >
          A superfood supplement
          <br />
          in every meal
        </Typography>

        <Typography
          sx={{
            color: QUIZ.color.ink,
            fontFamily: QUIZ.font.body,
            fontSize: {
              xs: "13.5px",
              md: "17px",
            },
            lineHeight: 1.45,
            mt: 3,
            mx: "auto",
            maxWidth: {
              xs: 440,
              md: 480,
            },
          }}
        >
          {supplementIntro}, already stirred through{" "}
          {dogPossessive} food.
          <br />
          Nothing to buy, nothing to sprinkle, nothing to
          remember.
        </Typography>
      </Box>

      {/* GREEN BANNER */}

      <Box
        sx={{
          bgcolor: QUIZ.color.green,
          borderRadius: "12px",
          mt: { xs: 3, md: 0 },
          mb: { md: 3 },
          mx: "auto",
          maxWidth: {
            xs: 440,
            md: 624,
          },
          px: {
            xs: 2,
            md: 4,
          },
          py: {
            xs: 1.75,
            md: 2.5,
          },
          textAlign: "center",
          gridColumn: {
            md: "1 / -1",
          },
          gridRow: {
            md: "2",
          },
        }}
      >
        <Typography
          sx={{
            color: QUIZ.color.brown,
            fontFamily: QUIZ.font.display,
            fontSize: {
              xs: "17px",
              md: "22px",
            },
            fontWeight: 900,
            lineHeight: 1.05,
            textTransform: "uppercase",
          }}
        >
          Included for free. Worth £30 a month.
        </Typography>

        <Typography
          sx={{
            color: QUIZ.color.brown,
            fontFamily: QUIZ.font.body,
            fontSize: {
              xs: "13.5px",
              md: "17px",
            },
            mt: 1,
            mx: {
              md: "auto",
            },
            maxWidth: {
              md: 560,
            },
          }}
        >
          That's what {supplementLabel} cost bought
          separately. They're in every meal, on every
          plan.
        </Typography>
      </Box>

      {/* DOG IMAGE */}

      <Box
        sx={{
          display: { md: "flex" },
          flexDirection: { md: "column" },
          mt: {
            xs: 3,
            md: 0,
          },
          mx: {
            xs: "auto",
            md: 0,
          },
          maxWidth: {
            xs: "100%",
            md: "none",
          },
          gridColumn: {
            md: "1",
          },
          gridRow: {
            md: "3",
          },
        }}
      >
        <Box
          component="img"
          src={quizAsset("hero-supplement-dog")}
          alt={`${dogDisplayName} with a bowl of Paws on Plates food`}
          sx={{
            aspectRatio: {
              xs: "1 / 1",
              md: "1 / 1",
            },
            borderRadius: "10px",
            display: "block",
            height: "auto",
            flexShrink: 0,
            objectFit: "cover",
            objectPosition: "center",
            width: "100%",
          }}
        />

        <Typography
          sx={{
            color: "#8B7C75",
            fontFamily: QUIZ.font.body,
            fontSize: {
              xs: "12.5px",
              md: "16.5px",
            },
            mt: { xs: 1, md: "auto" },
            pt: { md: 1 },
            textAlign: "center",
          }}
        >
          The supplement is stirred through the food
          itself, not sprinkled on top.
        </Typography>
      </Box>

      {/* SUPPLEMENT CARDS */}

      <Box
        sx={{
          display: {
            xs: "flex",
            md: "grid",
          },
          flexDirection: {
            xs: "column",
          },
          gridTemplateColumns: {
            md: "1fr 1fr",
          },
          gap: {
            xs: 1.5,
            md: 2,
          },
          mt: {
            xs: 3,
            md: 0,
          },
          mx: {
            xs: "auto",
            md: 0,
          },
          maxWidth: {
            xs: 620,
            md: "none",
          },
          gridColumn: {
            md: "2",
          },
          gridRow: {
            md: "3",
          },
        }}
      >
        {supplements.length > 0 ? (
          supplements.map((supplement) => (
            <SupplementCard
              key={supplement.id}
              supplement={supplement}
            />
          ))
        ) : (
          <Typography
            sx={{
              color: QUIZ.color.brown,
              fontFamily: QUIZ.font.body,
              textAlign: "center",
            }}
          >
            Supplement details are not available right
            now.
          </Typography>
        )}
      </Box>

      {/* NO EXTRA COST */}

      <Typography
        sx={{
          bgcolor: QUIZ.color.cream2,
          borderRadius: "12px",
          color: QUIZ.color.brown,
          fontFamily: QUIZ.font.body,
          fontSize: {
            xs: "13.5px",
            md: "17px",
          },
          lineHeight: 1.45,
          mt: {
            xs: 3,
            md: 0,
          },
          mx: {
            xs: "auto",
            md: 0,
          },
          maxWidth: {
            xs: 620,
            md: "none",
          },
          p: {
            xs: 2,
            md: 2.5,
          },
          textAlign: {
            xs: "left",
            md: "center",
          },
          gridColumn: {
            md: "1 / -1",
          },
          gridRow: {
            md: "4",
          },
        }}
      >
        <strong>No extra cost, no extra step.</strong>{" "}
        Most owners buying these separately are paying
        for {supplementCount || "multiple"} products,
        deliveries and things to remember. They're
        already in the food, portioned for{" "}
        {dogDisplayName}, in every single meal.
      </Typography>

      {/* REVIEW */}

      <Box
        sx={{
          bgcolor: "#111",
          borderRadius: "10px",
          mt: {
            xs: 3,
            md: 0,
          },
          mb: {
            md: 3,
          },
          mx: {
            xs: "auto",
            md: 0,
          },
          maxWidth: {
            xs: 620,
            md: "none",
          },
          p: 2.5,
          textAlign: "left",
          gridColumn: {
            md: "1 / -1",
          },
          gridRow: {
            md: "5",
          },
        }}
      >
        {/* Stars + heading:
            mobile = separate lines
            desktop = same line
        */}

        <Box
          sx={{
            display: "flex",

            flexDirection: {
              xs: "column",
              md: "row",
            },

            alignItems: {
              xs: "flex-start",
              md: "center",
            },

            gap: {
              xs: 0.75,
              md: 2,
            },

            mb: 1,
          }}
        >
          <Box
            sx={{
              color: "#fff",
              display: "flex",
              gap: "2px",
              flexShrink: 0,
              lineHeight: 1,
            }}
            aria-label="5 out of 5 stars"
          >
            {"★★★★★"}
          </Box>

          <Typography
            sx={{
              color: "#fff",
              fontFamily: QUIZ.font.display,
              fontSize: {
                xs: "14px",
                md: "18px",
              },
              fontWeight: 900,
              lineHeight: 1,
              textTransform: "uppercase",
              whiteSpace: {
                md: "nowrap",
              },
            }}
          >
            Real health improvements
          </Typography>
        </Box>

        <Typography
          sx={{
            color: "#aaa",
            fontFamily: QUIZ.font.body,
            fontSize: "0.85rem",
            lineHeight: 1.4,
            mt: 0.5,
          }}
        >
          "It's made a noticeable difference to my ten
          year old: better stools, her coat shines, and
          she has more energy."
        </Typography>

        <Typography
          sx={{
            color: "#aaa",
            fontFamily: QUIZ.font.body,
            fontSize: "0.72rem",
            mt: 1,
          }}
        >
          Wendy · Verified Trustpilot review
        </Typography>
      </Box>
    </Box>
  );
}